import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { easing } from "../../lib/animations";

/**
 * Hero — full-bleed colour video plays as a continuation that leads into
 * the longer Field Study showreel. Clicking the video smooth-scrolls to it.
 * Bottom-left shows live cursor coords; bottom-right shows a quiet
 * Field Study play link.
 */
const VIDEO_SOURCES = [
    { src: "/assets/yk/header.mp4", type: "video/mp4" },
];

export const Hero = ({ ready }) => {
    const videoRef = useRef(null);
    const [cursor, setCursor] = useState({ x: 1000, y: 2000 });

    useEffect(() => {
        if (!ready) return;
        const v = videoRef.current;
        if (!v) return;
        let cancelled = false;
        const startAtOffset = () => {
            if (cancelled) return;
            try {
                if (v.currentTime < 0.05) v.currentTime = 0.5;
                // 2× slower playback — deliberate cinematic motion without feeling stuck
                v.playbackRate = 0.5;
            } catch (e) {
                /* some browsers throw before metadata is ready */
            }
            const p = v.play();
            if (p && typeof p.catch === "function") p.catch(() => {});
        };
        if (v.readyState >= 1) {
            startAtOffset();
        } else {
            v.addEventListener("loadedmetadata", startAtOffset, { once: true });
        }
        return () => {
            cancelled = true;
            v.removeEventListener("loadedmetadata", startAtOffset);
        };
    }, [ready]);

    useEffect(() => {
        const move = (e) => setCursor({ x: e.clientX, y: e.clientY });
        window.addEventListener("mousemove", move);
        return () => window.removeEventListener("mousemove", move);
    }, []);

    const scrollToShowreel = () => {
        const el = document.querySelector("#showreel");
        if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return (
        <section className="hero-canvas" id="home" data-testid="hero-section">
            {/* Background video — full colour, click to jump to Field Study */}
            <video
                ref={videoRef}
                muted
                playsInline
                loop
                preload="auto"
                data-testid="hero-video"
                onClick={scrollToShowreel}
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    zIndex: 1,
                    cursor: "pointer",
                    filter: "contrast(1.04) brightness(0.92) saturate(1.02)",
                }}
            >
                {VIDEO_SOURCES.map((s) => (
                    <source key={s.src} src={s.src} type={s.type} />
                ))}
            </video>

            {/* Soft vignette */}
            <div
                aria-hidden="true"
                style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 2,
                    background:
                        "radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 100%)",
                    pointerEvents: "none",
                }}
            />

            {/* Top-left brand stamp */}
            <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={ready ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.7, ease: easing.primary }}
                aria-hidden="true"
                style={{
                    position: "absolute",
                    top: 110,
                    left: "clamp(28px, 4vw, 56px)",
                    zIndex: 4,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 14,
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 10,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.7)",
                }}
                data-testid="hero-brand-stamp"
            >
                <motion.span
                    aria-hidden="true"
                    animate={{ opacity: [1, 0.2, 1] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        width: 6,
                        height: 6,
                        background: "#c1121f",
                        display: "inline-block",
                        boxShadow: "0 0 4px rgba(193, 18, 31, 0.55)",
                    }}
                />
                Proof — Field 001
            </motion.div>

            {/* LEFT manifesto block: small noise copy + big LESS NOISE */}
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={ready ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 1.1, delay: 1.0, ease: easing.primary }}
                data-testid="hero-noise-block"
                style={{
                    position: "absolute",
                    left: "clamp(28px, 4vw, 56px)",
                    bottom: "clamp(120px, 16vw, 220px)",
                    zIndex: 4,
                    maxWidth: "min(46ch, 44vw)",
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    gap: "clamp(18px, 2.2vw, 28px)",
                    pointerEvents: "none",
                }}
            >
                <p
                    style={{
                        margin: 0,
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: "clamp(11px, 0.9vw, 13px)",
                        letterSpacing: "0.18em",
                        textTransform: "uppercase",
                        lineHeight: 1.7,
                        color: "rgba(255,255,255,0.78)",
                        maxWidth: "44ch",
                    }}
                >
                    Everyone is talking. Brands. Algorithms. Experts. More trends.
                    More opinions. More reasons to doubt yourself.
                </p>
                <h1
                    style={{
                        margin: 0,
                        fontFamily: "'Space Grotesk', Manrope, sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(56px, 8.2vw, 152px)",
                        letterSpacing: "-0.045em",
                        lineHeight: 0.9,
                        textTransform: "uppercase",
                        color: "#fff",
                    }}
                >
                    Less Noise
                </h1>
            </motion.div>

            {/* Bottom row */}
            <div
                style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 5,
                    padding: "24px clamp(28px, 4vw, 56px) 28px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end",
                    pointerEvents: "none",
                    fontFamily: "JetBrains Mono, monospace",
                    color: "rgba(255, 255, 255, 0.85)",
                    fontSize: 10,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                }}
            >
                {/* Bottom-left: live cursor coordinates */}
                <motion.span
                    initial={{ opacity: 0, y: 10 }}
                    animate={ready ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 0.9, duration: 0.7, ease: easing.primary }}
                    data-testid="hero-coords"
                    style={{
                        pointerEvents: "none",
                        opacity: 0.75,
                    }}
                >
                    [ X : {String(Math.floor(cursor.x)).padStart(4, "0")} &nbsp;-&nbsp; Y :{" "}
                    {String(Math.floor(cursor.y)).padStart(4, "0")} ]
                </motion.span>

                {/* Bottom-right: Field Study play link */}
                <motion.a
                    href="#showreel"
                    onClick={(e) => {
                        e.preventDefault();
                        scrollToShowreel();
                    }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={ready ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: 1.1, duration: 0.7, ease: easing.primary }}
                    data-testid="hero-play-showreel"
                    style={{
                        pointerEvents: "auto",
                        color: "rgba(255,255,255,0.95)",
                        textDecoration: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        fontSize: 10,
                    }}
                >
                    <span
                        aria-hidden="true"
                        style={{
                            width: 26,
                            height: 26,
                            borderRadius: 0,
                            border: "1px solid rgba(255,255,255,0.85)",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <svg width="8" height="9" viewBox="0 0 8 9" fill="none">
                            <path d="M0.5 0.5L7.5 4.5L0.5 8.5V0.5Z" fill="#fff" />
                        </svg>
                    </span>
                    Field&nbsp;Study
                    <span style={{ opacity: 0.55 }}>&nbsp;00 : 48</span>
                </motion.a>
            </div>
        </section>
    );
};

export default Hero;
